import { HttpClient } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import * as L from 'leaflet';
import {
  CRS,
  Circle,
  Control,
  Draw,
  Edit,
  FeatureGroup,
  GeometryUtil,
  LatLng,
  LatLngBounds,
  Map,
  Marker,
  Polyline,
  Rectangle,
  control,
  divIcon,
  imageOverlay,
  latLng,
  marker,
} from 'leaflet';
import 'leaflet-arrowheads';
import 'leaflet-draw';
import 'leaflet-easybutton';
import { ngxLoadingAnimationTypes } from 'ngx-loading';
import { ToastrService } from 'ngx-toastr';
import { Subject, from, of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { FlagService } from '../../flag/flag.service';
import { SettingService } from '../../setting/setting.service';
import { AddFlagComponent } from '../add.flag/add.flag.component';
import { EditFlagComponent } from '../edit.flag/edit.flag.component';
import { PictureService } from '../picture.service';

const PrimaryWhite = '#ffffff';
const SecondaryGrey = '#ccc';

@Component({
  selector: 'app-picture-viewer',
  templateUrl: './picture-viewer.component.html',
  styleUrls: ['./picture-viewer.component.scss'],
})
export class PictureViewerComponent implements OnInit, OnChanges {
  private rotateSubject = new Subject<{ id: number; key: string; degree: number }>();
  public ngxLoadingAnimationTypes = ngxLoadingAnimationTypes;
  public primaryColour = PrimaryWhite;
  public secondaryColour = SecondaryGrey;

  constructor(
    private httpClient: HttpClient,
    private domSanitizer: DomSanitizer,
    private toastr: ToastrService,
    private pictureService: PictureService,
    private zone: NgZone,
    public settingService: SettingService,
    public flagService: FlagService,
    private renderer: Renderer2,
    private cdr: ChangeDetectorRef,
  ) {
    this.rotateSubject
      .pipe(
        debounceTime(1000),
        tap(params => (this.loading = true)),
        mergeMap(params => from(this.pictureService.rotate(params)).pipe(catchError(error => of(error)))),
        withLatestFrom(this.rotateSubject.pipe(map(params => params.id))),
        tap(([response, id]) => {
          if (response?.id) {
            this.picture.Location = response.Location;
            this.picture.key = response.key;
            this.picture.versions = response.versions;
            this.picture.updatedAt = response.updatedAt;
            this.setImg(this.picture.Location);
            this.setVersion();
          }
          this.picture.degree = 0;
          this.markupChanged.emit(this.picture);
          this.loading = false;
        }),
      )
      .subscribe();
  }

  get selectedVersion() {
    return this._selectedVersion;
  }
  set selectedVersion(version) {
    this._selectedVersion = version;
    if (version) {
      this.setImg(version.Location);
      this.updateDefaultKey(version);
    }
  }

  @ViewChild('flagTemplate', { static: true }) flagTemplate: ElementRef;
  @ViewChild('addFlagModal', { static: false }) addFlagModal: AddFlagComponent;
  @ViewChild('editFlagModal', { static: false }) editFlagModal: EditFlagComponent;

  lOptions: any = {
    zoom: -1.4,
    wheelPxPerZoomLevel: 120,
    zoomSnap: 0.1,
    zoomDelta: 0.4,
    doubleClickZoom: false,
    minZoom: -5,
    center: latLng(500, 1000),
    crs: CRS.Simple,
  };

  lLayers: any[] = [];
  lFlags = new FeatureGroup();
  lDrawing = new FeatureGroup();

  map: Map = null;
  imgSize;
  img;
  icon;

  lastMouseUp;
  loading = false;

  versions = [];

  _selectedVersion;

  bounds: any = [
    [0, 0],
    [1, 1],
  ];

  @Input() flags: any[];
  @Input() flagsWithTooltip: any[] = [];
  @Input() picture: any;
  @Input() placeholderFlag: any;
  @Input() updateImage: any;
  @Input() hideWriteControls = false;
  @Input() hideFlagText = false;
  @Input() hideEasyButton = false;
  @Input() freezeFlags = false;
  @Input() isFullscreen = false;
  @Input() disableFullscreen = false;
  @Output() addFlag = new EventEmitter<{ x: number; y: number; clientX: number; clientY: number }>();
  @Output() editFlag = new EventEmitter<any>();
  @Output() moveFlag = new EventEmitter<any>();
  @Output() blobLoaded = new EventEmitter<Blob>();
  @Output() markupChanged = new EventEmitter<any>();
  @Output() changeFullscreen = new EventEmitter<void>();

  isDrawing = false;
  drawControl;
  minZoom;
  flagsOnMarkupEnabled = true;
  myEasyButton;

  ngOnChanges(ch) {
    if ('flags' in ch) {
      if (JSON.stringify(ch['flags'].currentValue) === JSON.stringify(ch['flags'].previousValue)) {
        delete ch.flags;
      }
    }

    if (this.picture && 'picture' in ch) {
      // Remove image from map
      this.lLayers = [];
      this.setImg(this.picture.Location);
      this.setMarkup();
      this.setVersion();
    }

    if ('flags' in ch || 'placeholderFlag' in ch || 'flagsWithTooltip' in ch) {
      this.setFlags(this.flags || []);
    }

    if ('updateImage' in ch) {
      this.resetImage();
    }

    if ('isFullscreen' in ch) {
      this.updateFullscreenIcon();
    }
  }

  resetImage() {
    if (this.map) {
      if (this.picture && this.picture.Location) {
        this.setImg(this.picture.Location);
      }
      this.map.invalidateSize();
    }
  }

  setVersion() {
    this.versions = this.picture.versions;
    this._selectedVersion = this.versions && this.versions.find(v => v.Key === this.picture.key);
  }

  async ngOnInit() {}

  async setImg(src: string) {
    this.loading = true;
    const imgData = await this.httpClient
      .get(`${src}`, { responseType: 'blob' })
      .pipe(
        map(e => {
          this.blobLoaded.emit(e);
          return this.domSanitizer.bypassSecurityTrustUrl(URL.createObjectURL(e));
        }),
      )
      .toPromise();

    this.imgSize = await this.getImageSize((imgData as any).changingThisBreaksApplicationSecurity);
    this.bounds = [[0, 0], this.imgSize];
    const imgLayer = imageOverlay((imgData as any).changingThisBreaksApplicationSecurity, this.bounds);

    this.lLayers = [imgLayer, this.lDrawing, this.lFlags];

    if (this.map) {
      this.map.invalidateSize().setMaxBounds(this.bounds);

      const scaleW = this.map.getSize().x / this.bounds[1][1];
      const zoomW = this.map.getScaleZoom(scaleW, 0);

      const scaleH = this.map.getSize().y / this.bounds[1][0];
      const zoomH = this.map.getScaleZoom(scaleH, 0);
      this.minZoom = Math.min(zoomH, zoomW);
      this.map.setMinZoom(this.minZoom);
      this.map.setZoom(this.minZoom);

      this.addRotateButton();
      if (!this.lFlags.getLayers().length && !this.lDrawing.getLayers().length) {
        this.enableRotateButton();
      } else {
        this.disableRotateButton();
      }
      if (this.picture?.degree) {
        setTimeout(() => {
          this.rotateUIImage(this.picture.degree);
        }, 500);
      }
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  styleRotateButton() {
    this.myEasyButton.setPosition('topright').addTo(this.map);
    this.myEasyButton.button.style.backgroundColor = '#ffffff';
    this.myEasyButton.button.style.color = '#464646';
    this.myEasyButton.button.style.border = 'none';
    this.myEasyButton.button.style.borderRadius = '2px';
    this.myEasyButton.button.style.width = '30px';
    this.myEasyButton.button.style.height = '30px';

    this.myEasyButton.button.addEventListener('mouseover', () => {
      this.myEasyButton.button.style.backgroundColor = '#f5f5f5';
    });
    this.myEasyButton.button.addEventListener('mouseout', () => {
      this.myEasyButton.button.style.backgroundColor = '#ffffff';
    });

    this.styleRotateButtonContainer();
  }

  styleRotateButtonContainer() {
    const easyRotateButtonContainer = document.querySelector('.easy-button-container') as HTMLElement;
    if (easyRotateButtonContainer) {
      easyRotateButtonContainer.style.marginRight = '289px';
      easyRotateButtonContainer.style.marginTop = '-40px';
      easyRotateButtonContainer.style.border = 'none';
      easyRotateButtonContainer.style.borderRadius = '2px';
      easyRotateButtonContainer.style.boxShadow = '0px 0px 5px 2px rgba(200, 200, 200, 0.3)';
    }
  }

  addRotateButton() {
    if (this.hideEasyButton || this.myEasyButton) {
      return;
    }
    this.myEasyButton = L.easyButton('fa-rotate-right', () => {
      this.rotate();
    });
    this.styleRotateButton();
  }

  async setFlags(flags: any[]) {
    this.lFlags.clearLayers();

    for (const flag of flags) {
      const draggableOption = !this.freezeFlags;
      const newMarker = marker([flag.x, flag.y], { draggable: draggableOption, icon: await this.flagMarker(flag) });

      (newMarker as any).flag = flag;

      // Move flag
      newMarker.on('dragstart', e => {
        console.log('dragstart', e);
        return false;
      });
      newMarker.on('dragend', e => {
        console.log('marker dragged');

        const icon: Marker = e.target;
        const position = icon.getLatLng();

        // Keep inside image bounds
        const x = Math.trunc(Math.max(0, Math.min(position.lat, this.imgSize[0])));
        const y = Math.trunc(Math.max(0, Math.min(position.lng, this.imgSize[1])));
        icon.setLatLng({ lat: x, lng: y });

        this.zone.run(() => {
          this.moveFlag.emit({ flag, x, y });
        });
      });

      // Edit flag
      newMarker.on('click', (e: any) => {
        const clientX = e.originalEvent.clientX;
        const clientY = e.originalEvent.clientY;

        this.editFlag.emit({ flag, clientX, clientY });
      });

      this.lFlags.addLayer(newMarker);
    }

    if (this.placeholderFlag) {
      const newMarker = marker([this.placeholderFlag.x, this.placeholderFlag.y], { icon: this.placeholderMarker() });
      this.lFlags.addLayer(newMarker);
    }
    this.enableOrDisableRotateButton();
  }

  enableOrDisableRotateButton() {
    if ((this.lFlags && this.lFlags.getLayers().length) || (this.lDrawing && this.lDrawing.getLayers().length)) {
      this.disableRotateButton();
      return;
    }
    if (this.map) {
      this.addRotateButton();
      this.enableRotateButton();
      this.map.addControl(this.myEasyButton);
      this.styleRotateButtonContainer();
    }
  }

  enableRotateButton() {
    if (this.myEasyButton) {
      this.myEasyButton.button.style.backgroundColor = '#ffffff';
      this.myEasyButton.button.style.color = '#464646';
      this.myEasyButton.button.disabled = false;
      this.myEasyButton.button.style.cursor = 'pointer';
    }
  }

  disableRotateButton() {
    if (this.myEasyButton) {
      this.myEasyButton.button.style.backgroundColor = '#f4f4f4';
      this.myEasyButton.button.style.color = '#bbbbbb';
      this.myEasyButton.button.disabled = true;
      this.myEasyButton.button.style.cursor = 'default';
    }
  }

  private async flagMarker(flag) {
    let showTooltip = false;
    if (this.flagsWithTooltip.includes(flag.id)) {
      showTooltip = true;
    }

    const html = this.flagTemplate.nativeElement.cloneNode(true) as HTMLDivElement;

    html.style.display = 'block';

    if (!this.hideFlagText) {
      html.getElementsByClassName('flag-text')[0].innerHTML = flag.text;
    }

    if (showTooltip) {
      html.getElementsByClassName('flag-warning-tooltip')[0].innerHTML = 'move me wherever you want';
    }
    const subtypeInfo = await this.flagService.subtypeInfo;

    html.getElementsByClassName('flag-type')[0].innerHTML =
      `${flag.subtype && flag.subtype.type && flag.subtype.type.name} - ${this.flagService.getSubtypeInfo(subtypeInfo, flag.subtype.name).name}`;

    return divIcon({ html });
  }

  private placeholderMarker() {
    const html = this.flagTemplate.nativeElement.cloneNode(true) as HTMLDivElement;
    html.style.display = 'block';
    html.getElementsByClassName('flag-text')[0].parentElement.remove();
    return divIcon({ html });
  }

  @HostListener('window:resize', ['$event'])
  onResize(ev) {
    if (this.map) {
      this.map.invalidateSize();
      if (this.picture && this.picture.Location) {
        this.setImg(this.picture.Location);
      }
    }
  }

  async addFlagWhileDrawing(center: LatLng, element: HTMLImageElement) {
    this.flagsOnMarkupEnabled = await this.settingService.flagsOnMarkupEnabled();
    if (!this.flagsOnMarkupEnabled) {
      return;
    }
    const x = center.lat;
    const y = center.lng;
    const positions = element.getBoundingClientRect();
    const clientX = positions['x'];
    const clientY = positions['y'];
    const coords = { x, y, clientX, clientY };
    this.addFlag.emit(coords);
  }

  setElementStyles(element, styles) {
    // tslint:disable-next-line:forin
    for (const key in styles) {
      element.style[key] = styles[key];
    }
  }

  iterateChildNodes(parent, childTag, styles) {
    const childNodes = parent.querySelectorAll(childTag);
    childNodes.forEach(child => this.setElementStyles(child, styles));
  }

  styleZoomButtons() {
    const selector = '.leaflet-control-zoom';
    const selectedControl = document.querySelector(selector);

    if (selectedControl) {
      this.setElementStyles(selectedControl, {
        display: 'flex',
        flexDirection: 'row',
        marginRight: '505px',
        border: 'none',
      });

      this.iterateChildNodes(selectedControl, 'a', {
        marginRight: '7px',
        borderRadius: '2px',
        borderBottomWidth: '0px',
        lineHeight: '26px',
        fontSize: '20px',
        boxShadow: '0px 0px 5px 2px rgba(200, 200, 200, 0.3)',
      });
    }
  }

  styleToolbar() {
    const selector = '.leaflet-draw-toolbar.leaflet-bar';
    const selectedControl = Array.from(document.querySelectorAll(selector)).pop();

    if (selectedControl) {
      selectedControl.classList.add('new-class');
      const addedClass = document.querySelector('.new-class');
      this.setElementStyles(addedClass, {
        display: 'flex',
        flexDirection: 'row',
        marginTop: '-40px',
        marginRight: '165px',
        border: 'none',
      });

      this.iterateChildNodes(addedClass, 'a', {
        marginRight: '7px',
        borderRadius: '2px',
        borderBottomWidth: '0px',
        boxShadow: '0px 0px 5px 2px rgba(200, 200, 200, 0.3)',
      });
    }
  }

  styleFullscreenButton() {
    const container = document.querySelector('.leaflet-bar.leaflet-control') as HTMLElement;
    if (!container) {
      return;
    }

    const newDiv = this.createFullscreenDiv();
    if (this.disableFullscreen) {
      this.styleDisabledFullscreenDiv(newDiv);
    } else {
      this.styleEnabledFullscreenDiv(newDiv);
      this.attachFullscreenListeners(newDiv, container);
    }
    this.insertIntoContainer(newDiv, container);
  }

  createFullscreenDiv() {
    const newDiv = document.createElement('div');
    newDiv.className = 'fullscreen-class';

    this.icon = this.renderer.createElement('i');
    this.icon.className = this.isFullscreen ? 'fa fa-compress' : 'fa fa-expand';
    newDiv.appendChild(this.icon);

    return newDiv;
  }

  styleDisabledFullscreenDiv(div) {
    this.setElementStyles(div, {
      backgroundColor: '#f4f4f4',
      color: '#bbbbbb',
      cursor: 'default',
      disabled: true,
      ...this.getCommonFullscreenButtonStyles(),
    });
  }

  styleEnabledFullscreenDiv(div) {
    this.setElementStyles(div, {
      backgroundColor: '#ffffff',
      cursor: 'pointer',
      ...this.getCommonFullscreenButtonStyles(),
    });

    this.renderer.listen(div, 'mouseover', () => {
      this.renderer.setStyle(div, 'backgroundColor', '#f5f5f5');
    });
    this.renderer.listen(div, 'mouseout', () => {
      this.renderer.setStyle(div, 'backgroundColor', '#ffffff');
    });
  }

  attachFullscreenListeners(div, container) {
    this.renderer.listen(container, 'click', () => {
      this.changeFullscreen.emit();
    });
  }

  getCommonFullscreenButtonStyles() {
    return {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: '7px',
      border: 'none',
      borderRadius: '2px',
      height: '30px',
      width: '30px',
      boxShadow: '0px 0px 5px 2px rgba(200, 200, 200, 0.3)',
    };
  }

  insertIntoContainer(div, container) {
    if (container.firstChild) {
      container.insertBefore(div, container.firstChild);
    } else {
      container.appendChild(div);
    }
  }

  updateFullscreenIcon() {
    if (this.icon) {
      this.icon.className = this.isFullscreen ? 'fa fa-compress' : 'fa fa-expand';
    }
  }

  styleLineSquareCircleButtons() {
    const selector = '.leaflet-draw-toolbar-top';
    const selectedControl = document.querySelector(selector);

    if (selectedControl) {
      this.setElementStyles(selectedControl, {
        display: 'flex',
        flexDirection: 'row',
        marginTop: '-40px',
        border: 'none',
      });

      this.iterateChildNodes(selectedControl, 'a', {
        marginRight: '7px',
        borderRadius: '2px',
        borderBottomWidth: '0px',
        boxShadow: '0px 0px 5px 2px rgba(200, 200, 200, 0.3)',
      });
    }
  }

  styleDrawActions() {
    const selector = '.leaflet-draw-section';
    const selectedControl = document.querySelector(selector);

    if (selectedControl) {
      this.iterateChildNodes(selectedControl, 'ul', {
        marginTop: '33px',
      });
    }
  }

  styleEditDeleteButtons() {
    const selector = '.leaflet-draw.leaflet-control';
    const selectedControl = document.querySelector(selector);

    if (selectedControl) {
      this.setElementStyles(selectedControl, {
        display: 'flex',
        flexDirection: 'row',
        marginRight: '155px',
      });

      const sections = selectedControl.querySelectorAll('.leaflet-draw-section');

      if (sections.length >= 0) {
        this.iterateChildNodes(sections[1], 'ul', {
          marginTop: '33px',
          marginRight: '150px',
        });
      }
    }
  }

  styleMarkupButton() {
    const selector = '.leaflet-control-layers';
    const selectedControl = document.querySelector(selector);

    if (selectedControl) {
      this.setElementStyles(selectedControl, {
        display: 'flex',
        flexDirection: 'row',
        marginRight: '170px',
        marginTop: '-30px',
        border: 'none',
        borderRadius: '2px',
        paddingTop: '5px',
        height: '30px',
        width: '112px',
        boxShadow: '0px 0px 5px 2px rgba(200, 200, 200, 0.3)',
      });

      const childSection = selectedControl.querySelector('section');
      if (childSection) {
        this.setElementStyles(childSection, {
          height: '',
        });
      }
    }
  }

  onMapReady(map: Map) {
    this.map = map;
    this.map.zoomControl.setPosition('topright');

    // leaflet.draw patch (https://stackoverflow.com/questions/47720555/can-not-adjust-radius-in-leaflet-draw)
    (Edit.Circle.prototype as any)._resize = function (latlng) {
      const moveLatLng = this._moveMarker.getLatLng();
      this._shape.setRadius(this._map.distance(moveLatLng, latlng));
      this._map.fire(Draw.Event.EDITRESIZE, { layer: this._shape });
    };

    // Remove area/distance/radius information while drawing
    GeometryUtil.readableArea = () => '';
    GeometryUtil.readableDistance = () => '';
    (Draw.Circle.prototype as any)._onMouseMove = function (t) {
      const i = t.latlng;
      if ((this._tooltip.updatePosition(i), this._isDrawing)) {
        this._drawShape(i);
        if (this.options.showRadius) {
          this._tooltip.updateContent({
            text: this._endLabelText,
          });
        }
      }
    };

    // Remove polyline middle marker
    Edit.PolyVerticesEdit = Edit.PolyVerticesEdit.extend({ _createMiddleMarker: () => {} });

    if (!this.hideWriteControls) {
      // Add markup toggle
      control.layers({}, { 'Show markup': this.lDrawing }, { position: 'topright', collapsed: false }).addTo(map);

      // Start/stop drawing event
      map.on(Draw.Event.TOOLBAROPENED, e => {
        this.isDrawing = true;
      });
      map.on(Draw.Event.TOOLBARCLOSED, e => {
        setTimeout(() => {
          this.isDrawing = false;
        }, 100);
      });

      // Add drawing control
      this.drawControl = new Control.Draw({
        position: 'topright',
        edit: {
          featureGroup: this.lDrawing,
        },
        draw: {
          circlemarker: false,
          marker: false,
          polygon: false,
        },
      }).addTo(map);

      this.drawControl.setDrawingOptions({
        polyline: { shapeOptions: { color: '#f00', weight: 10 } },
        circle: { shapeOptions: { color: '#f00', fillOpacity: 0, weight: 10 } },
        rectangle: { shapeOptions: { color: '#f00', fillOpacity: 0, weight: 10 } },
      });

      setTimeout(() => {
        this.styleZoomButtons();
        this.styleToolbar();
        this.styleFullscreenButton();
        this.styleLineSquareCircleButtons();
        this.styleEditDeleteButtons();
        this.styleMarkupButton();
        this.styleDrawActions();
      }, 0);
    }

    map.on(Draw.Event.CREATED, (e: any) => {
      if (e.layer instanceof Circle) {
        this.lDrawing.addLayer(e.layer);
        const center = e.layer.getLatLng();
        const element = e.layer.getElement();
        this.addFlagWhileDrawing(center, element);
      } else if (e.layer instanceof Rectangle) {
        this.lDrawing.addLayer(e.layer);
        const center = e.layer.getCenter();
        const element = e.layer.getElement();
        this.addFlagWhileDrawing(center, element);
      } else if (e.layer instanceof Polyline) {
        e.layer.arrowheads({ size: '35px' });
        this.lDrawing.addLayer(e.layer);
      }
      this.updateMarkup();
    });

    map.on(Draw.Event.EDITED, e => this.updateMarkup());
    map.on(Draw.Event.DELETED, e => this.updateMarkup());

    // Handle polyline -> arrow
    map.on(Draw.Event.DRAWVERTEX, (e: any) => {
      // Get cancel action from draw control
      const actions = this.drawControl._toolbars.draw.getActions(e.layers.getLayers()[0]);
      const cancelDrawing = actions[2].callback.bind(actions[2].context);

      // get position
      const pos = e.layers.getLayers()[0].getLatLng();
      console.log(pos);

      const arrowJson = {
        type: 'polyline',
        latLngs: [
          { lat: pos.lat + 175, lng: pos.lng - 175 },
          { lat: pos.lat, lng: pos.lng },
        ],
      };

      const arrow = this.jsonToShape(arrowJson).arrowheads({ size: '35px' });
      this.lDrawing.addLayer(arrow);
      const element = e.layers.getLayers()[0].getElement();
      this.addFlagWhileDrawing(pos, element);

      setTimeout(() => {
        cancelDrawing();
        this.updateMarkup();
      }, 0);
    });

    map.on('click', (e: any) => {
      if (this.isDrawing) {
        return;
      }

      const x = e.latlng.lat;
      const y = e.latlng.lng;
      const clientX = e.originalEvent.clientX;
      const clientY = e.originalEvent.clientY;
      const coord = { x, y, clientX, clientY };

      if (this.coordIsInsidePicture(coord)) {
        this.addFlag.emit(coord);
      }
    });

    map.on('mouseup', (e: any) => {
      const x = e.latlng.lat;
      const y = e.latlng.lng;
      const clientX = e.originalEvent.clientX;
      const clientY = e.originalEvent.clientY;
      const coord = { x, y, clientX, clientY };

      if (!this.coordIsInsidePicture(coord)) {
        return;
      }

      this.lastMouseUp = coord;
      setTimeout(() => {
        this.lastMouseUp = null;
      }, 100);
    });
  }

  coordIsInsidePicture(coord) {
    return this.imgSize && coord.x >= 0 && coord.y >= 0 && coord.x <= this.imgSize[0] && coord.y <= this.imgSize[1];
  }

  shapeToJson(shape, type) {
    switch (type) {
      case 'rectangle':
        return {
          type,
          bounds: shape.getBounds(),
        };

      case 'circle':
        return {
          type,
          latLng: shape.getLatLng(),
          radius: shape.getRadius(),
        };

      case 'polyline':
        return {
          type,
          latLngs: shape.getLatLngs(),
        };
    }
  }

  jsonToShape(obj): any {
    switch (obj.type) {
      case 'rectangle':
        Object.setPrototypeOf(obj.bounds, LatLngBounds.prototype);
        return new Rectangle(obj.bounds, { color: '#f00', fillOpacity: 0, weight: 10 });

      case 'circle':
        return new Circle(obj.latLng, { radius: obj.radius, color: '#f00', fillOpacity: 0, weight: 10 });

      case 'polyline':
        return new Polyline(obj.latLngs, { color: '#f00', weight: 10 });
    }
  }

  updateMarkup() {
    this.zone.run(async () => {
      const markup = [];

      this.lDrawing.getLayers().forEach(l => {
        if (l instanceof Circle) {
          markup.push(this.shapeToJson(l, 'circle'));
        } else if (l instanceof Rectangle) {
          markup.push(this.shapeToJson(l, 'rectangle'));
        } else if (l instanceof Polyline) {
          const polyline = this.shapeToJson(l, 'polyline');
          polyline.latLngs = polyline.latLngs.map(l => {
            return {
              lat: l.lat,
              lng: l.lng,
            };
          });
          markup.push(polyline);
        }
      });
      this.picture.markup = markup;
      const updatePicture = await this.pictureService.updateMarkup(this.picture.id, markup);
      this.picture.updatedAt = updatePicture.updatedAt;
      this.picture.Location = updatePicture.Location;
      this.toastr.success('Markup updated');
      this.markupChanged.emit(this.picture);
      this.enableOrDisableRotateButton();
    });
  }

  async updateDefaultKey(version) {
    this.picture.key = version.Key;
    this.picture.Location = version.Location;
    if (!this.hideWriteControls) {
      await this.pictureService.updateDefaultKey(this.picture.id, version.Key);
      this.toastr.success('Default version updated');
    }
  }

  setMarkup() {
    this.lDrawing.clearLayers();
    // tslint:disable-next-line:no-unused-expression
    this.picture &&
      this.picture.markup &&
      this.picture.markup.forEach(m => {
        const shape = this.jsonToShape(m).addTo(this.lDrawing);
        if (m.type === 'polyline') {
          (shape as any).arrowheads({ size: '35px' });
        }
      });
  }

  async getImageSize(url: string) {
    return new Promise<any>(resolve => {
      const img = new Image();
      img.onload = (ev: any) => {
        resolve([ev.target.height, ev.target.width]);
      };
      img.src = url;
    });
  }

  openFlagById(id) {
    const flagMarker: any = this.lFlags.getLayers().find((l: any) => l.flag.id === id);
    if (flagMarker) {
      // Wait until it's rendered, so we get the icon position
      setTimeout(() => {
        const iconBounds = flagMarker._icon.getBoundingClientRect();
        flagMarker.fire('click', {
          originalEvent: {
            clientX: iconBounds.x,
            clientY: iconBounds.y,
          },
        });
      }, 500);
    }
  }

  rotateImage(id: number, key: string, degree: number) {
    this.rotateSubject.next({ id, key, degree });
  }

  rotateUIImage(degree) {
    const image = this.lLayers[0];
    if (!image || !image.getElement()) {
      setTimeout(() => {
        this.rotateUIImage(degree);
      }, 100);
      return;
    }
    image.getElement().style.transform = image
      .getElement()
      .style.transform.replace('rotate(0deg)', '')
      .replace('rotate(90deg)', '')
      .replace('rotate(180deg)', '')
      .replace('rotate(270deg)', '');

    image.getElement().style.transform += `rotate(${degree}deg)`;
    image.getElement().style.transformOrigin = '50% 50%';

    const width = image.getElement().width;
    const height = image.getElement().height;
    if (degree === 0 || degree === 180) {
      image.getElement().style.scale = '1';
    } else {
      if (width > height) {
        image.getElement().style.scale = '0.7';
      }
    }
  }

  async rotate() {
    this.picture.degree = (this.picture.degree + 90) % 360;
    this.rotateUIImage(this.picture.degree);
    this.rotateImage(this.picture.id, this.picture.key, this.picture.degree);
  }
}
